import React, {useCallback, useEffect, useRef, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';
import {fetchSamlMetadata} from 'src/openapi/admin/apis/SsoApi';
import {useAppDispatch} from 'src/store/hooks';

import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Input} from '@pb/kit-component-input-v2';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';

import {useValidateCertificate} from '../../../hooks/use-validate-certificate';
import {errorText, fieldGroup, loadingIndicator, successIndicator, tabContent} from '../upload-metadata.module.styl';
import {validateMetadataUrl} from '../utils/metadata-validation';

import {ProviderInstructions} from './provider-instructions';

export type UrlTabData = {
    url: string,
    loginUrl: string,
    certificate: string,
    valid: boolean,
};

type MetadataUrlTabProps = {
    data: UrlTabData,
    onChange: (data: UrlTabData) => void,
    providerId: string | null,
};

export const MetadataUrlTab: React.FC<MetadataUrlTabProps> = ({data, onChange, providerId}) => {
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [fetchLoading, setFetchLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const dispatch = useAppDispatch();
    const {validate, loading: certLoading, error: certError, clearError: clearCertError} = useValidateCertificate();
    const {metadataValidate} = useEventTracking();

    const loading = fetchLoading || certLoading;
    const displayError = fetchError ?? certError?.detailMessages?.[0] ?? null;

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    let inputColor: 'ERROR' | 'SUCCESS' | undefined;
    if (displayError != null) {
        inputColor = 'ERROR';
    } else if (data.valid) {
        inputColor = 'SUCCESS';
    }

    const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        abortControllerRef.current?.abort();
        setFetchLoading(false);
        setFetchError(null);
        clearCertError();
        onChange({url: event.target.value, loginUrl: '', certificate: '', valid: false});
    }, [onChange, clearCertError]);

    const handleBlur = useCallback(async () => {
        abortControllerRef.current?.abort();

        if (data.url.trim().length === 0) {
            setFetchError(null);
            clearCertError();
            setFetchLoading(false);
            onChange({...data, loginUrl: '', certificate: '', valid: false});
            return;
        }

        const result = validateMetadataUrl(data.url);
        if (!result.valid) {
            metadataValidate({method: 'url', valid: false});
            setFetchError(result.error);
            setFetchLoading(false);
            onChange({...data, loginUrl: '', certificate: '', valid: false});
            return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        setFetchError(null);
        clearCertError();
        setFetchLoading(true);

        try {
            const response = await dispatch(
                fetchSamlMetadata(
                    {fetchSamlMetadataRequest: {url: data.url}},
                    {signal: controller.signal},
                ),
            );

            if (controller.signal.aborted) {
                return;
            }

            if (response.payload?.success) {
                const {loginUrl, certificate} = response.payload.data;
                setFetchLoading(false);

                const certValid = await validate(certificate);
                metadataValidate({method: 'url', valid: certValid});
                if (certValid) {
                    onChange({url: data.url, loginUrl, certificate, valid: true});
                } else {
                    onChange({...data, loginUrl: '', certificate: '', valid: false});
                }
            } else {
                setFetchError(
                    'Could not retrieve metadata from this URL. '
                    + 'Verify the URL is correct and accessible, or use the Manual Entry tab.',
                );
                metadataValidate({method: 'url', valid: false});
                onChange({...data, loginUrl: '', certificate: '', valid: false});
            }
        } catch (err) {
            if ((err as {name?: string})?.name === 'AbortError') {
                return;
            }
            setFetchError('Failed to fetch metadata. Please check your connection and try again.');
            metadataValidate({method: 'url', valid: false});
            onChange({...data, loginUrl: '', certificate: '', valid: false});
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
            setFetchLoading(false);
        }
    }, [data, onChange, dispatch, validate, clearCertError, metadataValidate]);

    const handleDismiss = useCallback(() => {
        abortControllerRef.current?.abort();
        setFetchError(null);
        clearCertError();
        onChange({url: '', loginUrl: '', certificate: '', valid: false});
    }, [onChange, clearCertError]);

    const loadingText = fetchLoading ? 'Fetching metadata...' : 'Validating certificate...';

    return (
        <div className={tabContent}>
            <ProviderInstructions providerId={providerId} tabKey="url"/>

            {data.valid ? (
                <Group gap="xs" alignItems="center" className={successIndicator}>
                    <Icon type={Icons.CHECK} size="Size_14"/>
                    <span>IdP Login URL and signing certificate retrieved successfully</span>
                    <FlatButton
                        style={{marginLeft: 'auto'}}
                        onClick={handleDismiss}>
                        Clear IdP Metadata
                    </FlatButton>
                </Group>
            ) : (
                <div className={fieldGroup}>
                    <Label>Metadata URL</Label>
                    <Input
                        value={data.url}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="https://your-idp.com/metadata.xml"
                        color={inputColor}
                        disabled={loading}
                    />
                    {displayError != null ? <span className={errorText}>{displayError}</span> : null}
                    {loading ? (
                        <div className={loadingIndicator}>
                            <Loading type="ICON"/>
                            <span>{loadingText}</span>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};
